import type { GroupPrivate, GroupScheduleMeeting } from '@repo/types/groups';
import type { LocationPublic } from '@repo/types/locations';
import type { MeetingPublic } from '@repo/types/meetings';

import { cn } from '@repo/lib/cn';
import { scheduleSlotToLocal } from '@repo/lib/time/scheduleSlotTimezone';
import { format, getISODay, parseISO, setISODay } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';

import ScheduleMeetingCard from './ScheduleMeetingCard';

const ISO_WEEK_DAYS = [1, 2, 3, 4, 5, 6, 7];

/**
 * `repeat` is absent when an entry comes from a concrete meeting rather than a
 * group's recurring schedule — a one-off has no recurrence to label. `groupId`
 * and `groupName` are likewise optional, since not every source knows the
 * group (the parent schedule endpoint returns meetings without one).
 */
export interface ScheduleTimetableEntry extends Omit<GroupScheduleMeeting, 'repeat' | 'location'> {
  repeat?: GroupScheduleMeeting['repeat'];
  location?: Pick<LocationPublic, 'name'> | null;
  groupId?: number;
  groupName?: string;
  liveMeetingId?: number;
}

/**
 * Maps schedule element ids to the id of their meeting instance
 * that is currently taking place.
 */
function getLiveMeetingIdByScheduleId(meetings: MeetingPublic[]): Map<string, number> {
  const now = Date.now();
  const liveMeetingIds = new Map<string, number>();
  for (const meeting of meetings) {
    if (meeting.isCancelled || !meeting.scheduleId) continue;
    if (new Date(meeting.startDate).getTime() <= now && now <= new Date(meeting.endDate).getTime()) {
      liveMeetingIds.set(meeting.scheduleId, meeting.id);
    }
  }
  return liveMeetingIds;
}

/**
 * Flattens a group's schedule into timetable entries in local time, tagging
 * each with its group and the id of its currently live meeting instance.
 */
export function getScheduleTimetableEntries(group: Pick<GroupPrivate, 'id' | 'name' | 'meetings' | 'schedule'>): ScheduleTimetableEntry[] {
  if (!group.schedule) return [];
  const liveMeetingIds = getLiveMeetingIdByScheduleId(group.meetings);
  return group.schedule.meetings.map((meeting) => ({
    ...scheduleSlotToLocal(meeting),
    groupId: group.id,
    groupName: group.name,
    liveMeetingId: meeting.id ? liveMeetingIds.get(meeting.id) : undefined,
  }));
}

/**
 * Places concrete meetings on the weekly timetable.
 *
 * The group variant above flattens a *recurring* schedule; this one takes
 * meetings that have already happened or been scheduled — what the parent
 * endpoints return — and drops them onto the weekday they fall on. They carry
 * no recurrence and no group, so both are left unset.
 */
export function getMeetingTimetableEntries(
  meetings: Array<
    Pick<MeetingPublic, 'id' | 'name' | 'type' | 'startDate' | 'endDate' | 'meetingURL'> & {
      isCancelled?: boolean;
      location?: Pick<LocationPublic, 'name'> | null;
    }
  >
): ScheduleTimetableEntry[] {
  return meetings
    .filter((meeting) => !meeting.isCancelled)
    .map((meeting) => {
      const start = parseISO(meeting.startDate);
      const end = parseISO(meeting.endDate);

      return {
        id: String(meeting.id),
        name: meeting.name,
        type: meeting.type,
        day: getISODay(start),
        startT: format(start, 'HH:mm'),
        endT: format(end, 'HH:mm'),
        meetingURL: meeting.meetingURL,
        location: meeting.location ?? null,
      };
    });
}

interface ScheduleTimetableProps {
  meetings: ScheduleTimetableEntry[];
  showGroupName?: boolean;
  /**
   * Whether the day headings link into the calendar. Off for viewers who have
   * no calendar of their own to land on, such as parents.
   */
  linkDays?: boolean;
}

export default function ScheduleTimetable(scheduleTimetableProps: ScheduleTimetableProps) {
  const { meetings, showGroupName = false, linkDays = true } = scheduleTimetableProps;
  const today = new Date();
  const todayIsoDay = getISODay(today);

  const days = useMemo(() => {
    const grouped = new Map<number, ScheduleTimetableEntry[]>();
    for (const meeting of meetings) {
      const dayMeetings = grouped.get(meeting.day) ?? [];
      dayMeetings.push(meeting);
      grouped.set(meeting.day, dayMeetings);
    }
    return ISO_WEEK_DAYS.map((day) => ({
      day,
      meetings: (grouped.get(day) ?? []).sort((a, b) => a.startT.localeCompare(b.startT)),
    }));
  }, [meetings]);

  return (
    <Carousel opts={{ align: 'start', dragFree: true, containScroll: 'trimSnaps' }} className={'border-border-primary bg-surface-primary w-full rounded-2xl border'}>
      <div className={'flex items-center justify-end gap-2 border-b p-3 pb-4'}>
        <CarouselPrevious variant={'tertiary'} className={'static size-8 translate-x-0 translate-y-0'}>
          <ChevronLeft />
        </CarouselPrevious>
        <CarouselNext variant={'tertiary'} className={'static size-8 translate-x-0 translate-y-0'}>
          <ChevronRight />
        </CarouselNext>
      </div>

      <CarouselContent className={'first:ps-3'}>
        {days.map(({ day, meetings: dayMeetings }) => {
          const isToday = day === todayIsoDay;

          return (
            <CarouselItem key={day} className={cn('border-border-primary basis-4/5 border-l sm:basis-2/5 lg:basis-[30%] xl:basis-[22%]', isToday && 'bg-muted/25')}>
              <div className={'flex h-full flex-col gap-2 py-3 ps-0 pe-4'}>
                <div className={'flex items-center gap-2 px-1'}>
                  <h3 className={cn('text-foreground text-sm font-semibold capitalize', isToday && 'text-text-brand')}>
                    {linkDays ? (
                      <Link to={`/calendar?date=${format(setISODay(today, day), 'dd-MM-yyyy')}`} className={'hover:text-text-brand-hover hover:underline'}>
                        {format(setISODay(today, day), 'EEEE')}
                      </Link>
                    ) : (
                      format(setISODay(today, day), 'EEEE')
                    )}
                  </h3>
                  {isToday && (
                    <Badge variant={'brand'} border size={'sm'}>
                      Dziś
                    </Badge>
                  )}
                </div>
                {dayMeetings.length === 0 ? (
                  <p className={'text-muted-foreground px-1 text-xs'}>Brak zajęć</p>
                ) : (
                  <div className={'flex flex-col gap-2'}>
                    {dayMeetings.map((meeting, index) => (
                      <ScheduleMeetingCard key={meeting.id ?? `${meeting.groupId}-${meeting.name}-${meeting.startT}-${index}`} meeting={meeting} showGroupName={showGroupName} />
                    ))}
                  </div>
                )}
              </div>
            </CarouselItem>
          );
        })}
      </CarouselContent>
    </Carousel>
  );
}
